import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Loader2, Plus, UserPlus } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { api, apiError } from "@/lib/api";
import { useFreshest } from "@/lib/freshest";
import { AccessPanel } from "@/components/AccessPanel";
import { Expander } from "@/components/Expander";
import { useAuth } from "@/context/AuthContext";
import { TeamStats } from "@/components/people/TeamStats";
import { UnitTeam } from "@/components/people/UnitTeam";
import { GroupSection, WaitingSection } from "@/components/people/GroupSection";
import { MemberDialog } from "@/components/people/MemberDialog";
import { BulkInviteDialog } from "@/components/people/BulkInviteDialog";
import { PeopleSheet } from "@/components/people/PeopleSheet";
import { TrainedDialog } from "@/components/people/TrainedDialog";
import { CategoryToggles } from "@/components/people/CategoryToggles";
import { TeamReportDialog } from "@/components/people/TeamReportDialog";
import { GUEST, MANAGEMENT, PARTNER, SDTD, toBody } from "@/components/people/roles";

const ALL = "all";
const VIEWS = [
  ["all", "All teams"],
  ["management", "Management"],
  ["sdtd", "SDTD"],
  ["units", "Plants & units"],
  ["partners", "Pravartanam Partners"],
  ["guests", "Invited Members"],
];
const btn = "h-11 rounded-none font-display text-xs font-bold uppercase tracking-widest";

const Slicer = ({ value, onChange, options, testId, all }) => (
  <Select value={value || ALL} onValueChange={(v) => onChange(v === ALL ? "" : v)}>
    <SelectTrigger className="h-11 w-[200px] rounded-none" data-testid={testId}>
      <SelectValue />
    </SelectTrigger>
    <SelectContent className="max-h-72 rounded-none bg-popover">
      <SelectItem value={ALL}>{all}</SelectItem>
      {options.map(([v, l]) => (
        <SelectItem key={v} value={v} data-testid={`${testId}-option-${v.replace(/\s+/g, "-")}`}>
          {l}
        </SelectItem>
      ))}
    </SelectContent>
  </Select>
);

export default function People() {
  const { shows } = useAuth();
  const [params, setParams] = useSearchParams();
  const unit = params.get("unit") || "";
  const role = params.get("role") || "";
  const view = params.get("view") || "all";
  const [q, setQ] = useState("");
  const [data, setData] = useState(null);
  const [edit, setEdit] = useState({ open: false, person: null, fallback: {} });
  const [bulk, setBulk] = useState(false);
  const [trained, setTrained] = useState(null);
  const { start, isFresh } = useFreshest();

  const setParam = (k, v) => {
    const next = new URLSearchParams(params);
    if (v && v !== "all") next.set(k, v);
    else next.delete(k);
    setParams(next, { replace: true });
  };

  const load = () => {
    const ticket = start();
    api
      .get("/people/teams", { params: { q, unit, role } })
      .then((r) => isFresh(ticket) && setData(r.data))
      .catch((e) => isFresh(ticket) && toast.error(apiError(e)));
  };
  useEffect(() => {
    const t = setTimeout(load, 200);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, unit, role]);

  if (!data) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const jump = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
  const canAdd = shows("people.add") && (data.can_edit_all || data.units.some((u) => u.can_edit));
  const open = (person, fallback = {}) => setEdit({ open: true, person, fallback });
  const invite = async (p) => {
    if (!window.confirm(`Invite ${p.name} (${p.email}) as ${p.team_role}? A sign-in passcode is mailed to them.`)) return;
    try {
      await api.put("/people/member", {
        ...toBody(p), access_role: p.team === GUEST ? p.access || null : null, invite: true,
      });
      toast.success(`${p.name} invited.`);
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };
  const card = (canEdit) => ({
    canEdit,
    canInvite: data.can_edit_all && shows("people.invite"),
    showContacts: shows("people.contacts"),
    onEdit: (p) => open(p),
    onInvite: invite,
  });
  const filtered = !!(q || role || unit);
  const units = data.units.filter((t) => !filtered || t.count > 0);
  const roleOptions = [...new Set(Object.values(data.catalog).flat().map((r) => r.role))]
    .filter((r) => !data.hidden_roles.includes(r))
    .map((r) => [r, r]);
  const off = new Set(data.hidden_categories);
  const gone = {
    management: off.has("management"),
    sdtd: off.has("group_lead") && off.has("desk_officer"),
    partners: off.has("partners"),
    guests: off.has("guests"),
  };
  const see = (v) => !gone[v] && (view === "all" || view === v);

  return (
    <div className="p-5 sm:p-8" data-testid="people-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="label-caps">People</div>
          <h1 className="font-display text-3xl font-black tracking-tight sm:text-4xl">The digitalisation teams</h1>
          <p className="mt-2 max-w-3xl text-sm text-muted-foreground">
            Management, SDTD, every plant and unit, the Pravartanam partners and the invited members — team
            by team. A person&apos;s role in their team decides what they may do on the portal.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {canAdd && (
            <Button className={btn} onClick={() => open(null, { home: unit || (data.plants.length === 1 ? data.plants[0].code : "") })} data-testid="add-member-button">
              <Plus className="mr-2 h-4 w-4" /> Add a person
            </Button>
          )}
          {data.can_edit_all && shows("people.invite") && (
            <Button variant="outline" className={btn} onClick={() => setBulk(true)} data-testid="invite-officer-button">
              <UserPlus className="mr-2 h-4 w-4" /> Invite by e-mail
            </Button>
          )}
          {shows("people.excel") && <PeopleSheet canUpload={canAdd} onApplied={load} />}
          <TeamReportDialog data={data} unit={unit} />
        </div>
      </div>

      {data.can_toggle_categories && <CategoryToggles categories={data.categories} onChanged={load} />}

      <TeamStats data={data} onJump={jump} filtered={filtered} />

      <div className="mt-6 border border-border bg-card/95 p-3 backdrop-blur-md md:sticky md:top-0 md:z-20" data-testid="people-action-strip">
        <div className="flex flex-wrap items-center gap-2">
          <Input
            placeholder="Search a name, role, department or e-mail…"
            className="h-11 w-full max-w-sm rounded-none"
            data-testid="people-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
          <Slicer value={unit} onChange={(v) => setParam("unit", v)} options={data.plants.map((p) => [p.code, `${p.code} — ${p.name}`])} testId="people-unit-filter" all="Every unit" />
          <Slicer value={role} onChange={(v) => setParam("role", v)} options={roleOptions} testId="people-role-filter" all="Every role" />
          {filtered && (
            <button type="button" className="text-xs font-bold uppercase tracking-wider text-primary hover:underline" data-testid="people-filter-clear" onClick={() => { setQ(""); setParams({}, { replace: true }); }}>
              Clear
            </button>
          )}
        </div>
        <div className="mt-3 flex flex-wrap gap-1" data-testid="people-views">
          {VIEWS.filter(([v]) => !gone[v] && (v !== "guests" || data.can_edit_all)
            && (v !== "partners" || data.can_edit_all || data.partners.length > 0)).map(([v, l]) => (
            <button
              key={v}
              type="button"
              data-testid={`people-view-${v}`}
              onClick={() => setParam("view", v)}
              className={`border px-3 py-1 font-display text-[10px] font-bold uppercase tracking-widest transition-colors duration-200 ${
                view === v ? "border-foreground bg-foreground text-background" : "border-border hover:border-foreground"
              }`}
            >
              {l}
            </button>
          ))}
          {data.waiting.length > 0 && (
            <button type="button" onClick={() => jump("people-waiting")} data-testid="jump-waiting" className="border border-[hsl(var(--watch))] px-3 py-1 font-display text-[10px] font-bold uppercase tracking-widest text-[hsl(var(--watch))]">
              Waiting to be placed · {data.waiting.length}
            </button>
          )}
        </div>
      </div>

      <div className="mt-8 space-y-12" id="people-sections">
        {see("management") && (
          <GroupSection
            id="people-management"
            title="Management"
            note="Directors and Executive Directors"
            groups={data.management}
            card={card(data.can_edit_all)}
            empty="No Director or corporate Executive Director is on record yet. Press Add to Management, or add a row with Team = Management to the People sheet. A plant's own Executive Director goes in that unit's team (Unit team → Executive Director)."
            onAdd={canAdd && data.can_edit_all ? () => open(null, { team: MANAGEMENT }) : null}
          />
        )}
        {see("sdtd") && (
          <GroupSection
            id="people-sdtd"
            title="SDTD"
            note="Group Leads and Desk Officers of SDTD Ranchi — each is also shown in every unit they cover"
            groups={data.sdtd}
            card={card(data.can_edit_all)}
            empty="No Group Lead or Desk Officer is on record yet."
            onAdd={canAdd && data.can_edit_all ? () => open(null, { team: SDTD }) : null}
          />
        )}
        {see("units") && (
          <section id="people-units" data-testid="people-units" className="scroll-mt-24 space-y-10">
            <h2 className="font-display text-2xl font-black tracking-tight">Plants &amp; units</h2>
            {units.map((t) => (
              <UnitTeam key={t.plant} team={t} card={card(t.can_edit)} onTrained={setTrained} showTrained={shows("people.coursera") && !off.has("trained")} />
            ))}
            {!units.length && <p className="text-sm text-muted-foreground">No unit team matches these slicers.</p>}
          </section>
        )}
        {see("partners") && (data.can_edit_all || data.partners.length > 0) && (
          <GroupSection
            id="people-partners"
            title="Pravartanam Partners"
            note="The partner team (McKinsey) working on Pravartanam use cases"
            groups={data.partners}
            card={card(data.can_edit_all)}
            empty="No partner is on record yet."
            onAdd={canAdd && data.can_edit_all ? () => open(null, { team: PARTNER }) : null}
          />
        )}
        {see("guests") && data.can_edit_all && (
          <GroupSection
            id="people-guests"
            title="Invited Members"
            note="Guests outside the regular teams — SDTD picks what each of them may do on the portal"
            groups={data.guests}
            card={card(data.can_edit_all)}
            empty="No invited member yet. Use Invite by e-mail with Team = Invited Members, or add one here."
            onAdd={canAdd ? () => open(null, { team: GUEST }) : null}
          />
        )}
        {data.waiting.length > 0 && shows("people.pending") && (
          <WaitingSection people={data.waiting} card={card(data.can_edit_all)} />
        )}
      </div>

      {data.can_edit_all && shows("people.access") && (
        <div className="mt-12">
          <Expander
            title="Accounts & access"
            note="Sign-in status, passcodes over the phone, passwords put back, access switched off, and the queue of officers asking to see another unit."
            testId="people-access"
          >
            <AccessPanel />
          </Expander>
        </div>
      )}

      <MemberDialog
        open={edit.open}
        onOpenChange={(o) => setEdit((d) => ({ ...d, open: o }))}
        person={edit.person}
        fallback={edit.fallback}
        data={data}
        onSaved={load}
      />
      <BulkInviteDialog open={bulk} onOpenChange={setBulk} data={data} onDone={load} />
      <TrainedDialog team={trained} onClose={() => setTrained(null)} />
    </div>
  );
}
